/**
 * Switch port NUMBERING for display -- the one shared helper (owner ruling, 2026-09-13).
 *
 * uCentral names switch ports ZERO-based ("Ethernet0".."Ethernet27"). Operators, the switch CLI
 * (`interface ethernet 1/28`) and the front panel count from ONE, so a grid that says "0..27"
 * and "Uplink port Ethernet27" sends people to the wrong cable. Evidence from the switches' own
 * LLDP, read 2026-09-13: the EAP111 on ECS2100 Ethernet3 reports the switch port as
 * "unit 1, port 4"; ECS5550 Ethernet40 <-> ECS2100 "port 7"; ECS4150-54P Ethernet53 <-> ECS5550
 * "port 46". The controller renders ecCLI the same way (services/switch_portconfig._iface_number).
 *
 * PRESENTATION ONLY: API payloads, storage and every request keep the uCentral name. SFP ports
 * keep their faceplate numbers under the same rule (ECS4150-28P: Ethernet24-27 -> 25-28).
 * Pure and dependency-free so it runs under `node --test` (src/lib/switchPorts.test.ts).
 */

/** Exactly a uCentral port name: "Ethernet<N>" (no unit, no slash). */
const UCENTRAL_PORT = /^ethernet(\d+)$/i;
/** A uCentral port name inside free text, but not "Ethernet1/7"-style unit/port ids. */
const UCENTRAL_PORT_IN_TEXT = /\bEthernet(\d+)\b(?!\s*\/)/g;

/** "number" -> "28" (grids, chips); "cli" -> "1/28" (next to rendered switch commands). */
export type PortStyle = "number" | "cli";

/** True when `name` is a zero-based uCentral port name. */
export function isUcentralPortName(name: string | null | undefined): boolean {
  return name != null && UCENTRAL_PORT.test(name.trim());
}

/**
 * The operator-facing number of a switch port. "Ethernet27" -> "28" (or "1/28").
 * Names that are not uCentral port names ("1/1/11", "Eth 1/7", "WAN") pass through unchanged.
 */
export function formatSwitchPort(name: string | null | undefined, style: PortStyle = "number"): string {
  if (name == null) return "";
  const match = UCENTRAL_PORT.exec(name.trim());
  const digits = match?.[1];
  if (digits === undefined) return name;
  const oneBased = Number(digits) + 1;
  return style === "cli" ? `1/${oneBased}` : String(oneBased);
}

/**
 * Renumber every uCentral port name inside free text, e.g. an LLDP neighbour label or an
 * uplink description: "ECS4150 - Ethernet27" -> "ECS4150 - port 28". Other text is untouched
 * ("Ethernet Port on unit 1, port 41" is already 1-based and has no digits after "Ethernet").
 */
export function formatPortRefs(text: string | null | undefined): string {
  if (!text) return text ?? "";
  return text.replace(UCENTRAL_PORT_IN_TEXT, (_whole, digits: string) => `port ${Number(digits) + 1}`);
}
